import { useState, useRef, useMemo, useLayoutEffect } from 'react';
import { css } from '@emotion/react';
import { motion, AnimatePresence } from 'motion/react';
import { useTranslation } from '@frontend/i18n';
import { titleCase } from '@frontend/string';
import { theme } from '@frontend/theme';
import { NakedButton, Text } from '@frontend/design-system';
import { SelectionChip } from '../../../components/selection-chip';
import { CALENDAR_TRACKING_IDS } from '../../../trackingIds/calendar-tracking-ids';

const APPLIED_FILTERS_EXPANDED_MAX_HEIGHT = 260;

type FilterItem = {
  type: string;
  value: string;
  label: string;
};

type Props = {
  maxRows?: number;
  selectedFilters: FilterItem[];
  onRemove?: (type: string, value: string) => void;
};

export const CollapsibleFilterList = ({ selectedFilters, onRemove, maxRows = 2 }: Props) => {
  const { t } = useTranslation('scheduleCalendarFilters');
  const [isExpanded, setIsExpanded] = useState(false);
  const [collapsedHeight, setCollapsedHeight] = useState<number>();
  const [expandedHeight, setExpandedHeight] = useState<number>();
  const contentRef = useRef<HTMLDivElement>(null);
  const hiddenContentRef = useRef<HTMLDivElement>(null);
  const showLessButtonRef = useRef<HTMLButtonElement>(null);

  const { visibleFilters, hiddenCount } = useMemo(() => {
    if (selectedFilters.length === 0) {
      return { visibleFilters: selectedFilters, hiddenCount: 0 };
    }

    const estimatedFiltersPerRow = 2;
    const maxVisibleFilters = estimatedFiltersPerRow * maxRows;

    if (selectedFilters.length > maxVisibleFilters) {
      return {
        visibleFilters: selectedFilters.slice(0, maxVisibleFilters),
        hiddenCount: selectedFilters.length - maxVisibleFilters,
      };
    }

    return { visibleFilters: selectedFilters, hiddenCount: 0 };
  }, [selectedFilters, maxRows]);

  const filtersToShow = isExpanded ? selectedFilters : visibleFilters;

  const handleRemoveFilter = (type: string, value: string) => {
    onRemove?.(type, value);
  };

  // Scroll to bottom when expanded
  useLayoutEffect(() => {
    if (isExpanded && showLessButtonRef.current) {
      showLessButtonRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'end',
      });
    }
  }, [isExpanded]);

  // Measure heights after rendering
  useLayoutEffect(() => {
    if (contentRef.current && hiddenContentRef.current) {
      const collapsedRect = contentRef.current.getBoundingClientRect();
      const expandedRect = hiddenContentRef.current.getBoundingClientRect();

      setCollapsedHeight(collapsedRect.height);
      setExpandedHeight(Math.min(expandedRect.height, APPLIED_FILTERS_EXPANDED_MAX_HEIGHT));
    }
  }, [visibleFilters, selectedFilters, isExpanded]);

  return (
    <div css={{ position: 'relative' }}>
      {/* Visible container */}
      <motion.div
        initial={false}
        animate={{ height: isExpanded ? expandedHeight : collapsedHeight }}
        css={{ overflow: 'auto' }}
      >
        <div ref={contentRef} css={filterContainerStyle}>
          <AnimatePresence mode='popLayout'>
            {filtersToShow.map((item: FilterItem) => (
              <motion.div layout key={`${item.type}-${item.value}`} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                <SelectionChip
                  active
                  label={titleCase(item.label)}
                  onRemove={() => handleRemoveFilter(item.type, item.value)}
                />
              </motion.div>
            ))}
          </AnimatePresence>
          {hiddenCount > 0 && (
            <motion.div animate={{ opacity: 1 }}>
              <NakedButton
                ref={showLessButtonRef}
                trackingId={CALENDAR_TRACKING_IDS.headerBarFilter.appliedFiltersShowMoreLess}
                css={expandButtonStyle}
                onClick={() => setIsExpanded(!isExpanded)}
              >
                <Text size='small' color='primary'>
                  {isExpanded ? t('Show less') : t(`+{{ count }} more`, { count: hiddenCount })}
                </Text>
              </NakedButton>
            </motion.div>
          )}
        </div>
      </motion.div>
      {/* Hidden container for measurement */}
      <div
        ref={hiddenContentRef}
        css={{
          position: 'absolute',
          visibility: 'hidden',
        }}
      >
        <div css={filterContainerStyle}>
          {selectedFilters.map((item) => (
            <SelectionChip
              key={`hidden-${item.type}-${item.value}`}
              active
              label={titleCase(item.label)}
              onRemove={() => handleRemoveFilter(item.type, item.value)}
            />
          ))}
          <div>
            <NakedButton css={expandButtonStyle} onClick={() => setIsExpanded(false)}>
              <Text size='small' color='primary'>
                {t('Show less')}
              </Text>
            </NakedButton>
          </div>
        </div>
      </div>
    </div>
  );
};

const expandButtonStyle = css({
  whiteSpace: 'nowrap',
  textDecoration: 'underline',
  textDecorationColor: theme.colors.primary50,
});

const filterContainerStyle = css({
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(1),
});
